import axios from 'axios';
import type {
  LoginResponse,
  RegisterResponse,
  CreateConfigBody,
  SenderConfiguration,
  HandshakeStatus,
} from '@/types';

// API base URL
const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'https://optint.onexglobal.io';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwt_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear token from localStorage
      localStorage.removeItem('jwt_token');
      localStorage.removeItem('user_email');

      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API endpoints
export const authAPI = {
  login: async (email: string, password: string): Promise<LoginResponse> => {
    console.log('API Login attempt:', {
      email,
      endpoint: '/api/auth/optimove/login',
    });
    const response = await api.post('/api/auth/optimove/login', {
      email,
      password,
    });
    console.log('API Login response:', response.data);
    return response.data;
  },

  register: async (
    name: string,
    company: string,
    email: string,
    password: string
  ): Promise<RegisterResponse> => {
    const response = await api.post('/api/auth/optimove/register', {
      name,
      company,
      email,
      password,
    });
    return response.data;
  },

  forgotPassword: async (email: string): Promise<any> => {
    const response = await api.post('/api/auth/optimove/forgot-password', {
      email,
    });
    return response.data;
  },

  resetPassword: async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<any> => {
    const response = await api.post('/api/auth/optimove/reset-password', {
      token,
      password,
      confirmPassword,
    });
    return response.data;
  },
};

// Configuration API endpoints
export const configAPI = {
  getAll: async (): Promise<SenderConfiguration[]> => {
    const response = await api.get('/api/configs/optimove');
    console.log('API Configs - response:', response.data);
    return response.data.data.configs;
  },

  getById: async (
    id: string
  ): Promise<SenderConfiguration & { webhookUrls?: any }> => {
    console.log('API Config by ID - getting config:', {
      id,
      endpoint: `/api/configs/optimove/${id}`,
    });
    const response = await api.get(`/api/configs/optimove/${id}`);
    console.log('API Config by ID - response:', response.data);
    return {
      ...response.data.data.config,
      webhookUrls: response.data.data.webhookUrls,
    };
  },

  create: async (
    config: CreateConfigBody
  ): Promise<{ config: SenderConfiguration; handshake?: any }> => {
    const response = await api.post('/api/configs/optimove', config);
    return response.data.data;
  },

  update: async (
    id: string,
    config: Partial<CreateConfigBody>
  ): Promise<SenderConfiguration> => {
    const response = await api.put(`/api/configs/optimove/${id}`, config);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/api/configs/optimove/${id}`);
  },

  triggerHandshake: async (id: string): Promise<HandshakeStatus> => {
    const response = await api.post(`/api/configs/optimove/${id}/handshake`);
    return response.data.data;
  },

  getHandshakeStatus: async (id: string): Promise<HandshakeStatus> => {
    const response = await api.get(`/api/configs/optimove/${id}/handshake`);
    return response.data.data;
  },
};

// Webhook API endpoints
export const webhookAPI = {
  getStatus: async (): Promise<{
    status: string;
    timestamp: string;
    webhookEndpoints: {
      deliveryReceipt: string;
      inboundSms: string;
    };
    message: string;
  }> => {
    const response = await api.get('/api/webhook/status');
    return response.data;
  },
};

// Export default
export default api;
